import {useAuth} from '../../auth/auth'
import {useLocation, useNavigate} from 'react-router-dom'
import React, {useEffect, useMemo, useState} from 'react'
import {ActiveIteration, CountResult, ITER_NAMES, searchCounts} from '../utils'
import {getCachedProducts} from '../productCache'
import {
    Alert,
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography
} from '@mui/material'
import {API} from '../../common/axios'
import {webEvents} from '../../common/WebEvents'
import CheckIcon from '@mui/icons-material/Check'
import {useDispatch} from 'react-redux'
import { createError } from '../../store/slices/error'

const FLEX_ITEM_DETAILS_WIDTH = 'w-3/4 md:w-2/4'

function Conduct() {
    const {getTruck, getUser} = useAuth()
    const truckName = getTruck()
    const [activeIter, setActiveIter] = useState<ActiveIteration | null>(null)
    const [alertOpen, setAlertOpen] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    useEffect(() => {
        let submissionSuccessItem = window.sessionStorage.getItem('submissionSuccess')
        if (submissionSuccessItem === 'true') {
            setAlertOpen(true)
            setActiveIter(null)
            window.sessionStorage.setItem('submissionSuccess', 'false')
            return
        }
        let isMounted = true
        let activeIter: ActiveIteration | null = null

        window.addEventListener('storage', function(e) {
            if (e.storageArea === sessionStorage && e.key === 'Data') {
                // Something on another page changed the stored value.
            }
        })

        /* A truck can only have one active iteration. This means the number of counts
         * returned below can only be 0 or 1.
         */
        searchCounts({
            location_ids: truckName,
            iteration_statuses: 'pending,in-progress'
        })
            .then((counts: CountResult[]) => {
                if (!isMounted) return

                if (counts.length) {
                    let c = counts[0]
                    let iter = c.iteration

                    activeIter = {
                        countId: c.countId,
                        iterationId: iter.iterationId,
                        type: c.type,
                        status: iter.status,
                        sample: iter.sample,
                        description: c.description,
                        assignedAt: iter.assignedAt,
                        requestedBy: iter.requestedBy,
                        requestedAt: iter.requestedAt
                    }
                    window.sessionStorage.setItem('activeIter', JSON.stringify(activeIter))
                    window.sessionStorage.setItem('submissionSuccess', 'false')
                    setActiveIter(activeIter)
                }
            })
            .catch(error => {
                if (isMounted) {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error?.cause?.messages?.[0]
                        || error?.messages?.[0]
                        || 'Unknown error'

                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }
            })

        getCachedProducts()
            .then()
            .catch(error => {
                if (isMounted) {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error?.cause?.messages?.[0]
                        || error?.messages?.[0]
                        || 'Unknown error'

                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                }
            })

        return () => {
            isMounted = false
            window.sessionStorage.setItem('submissionSuccess', 'false')
        }
    }, [location.state])

    let action =
        activeIter?.status === 'pending' ? 'begin count' : 'resume count'

    const IterationDetails = useMemo(() => {
        if (!activeIter) return null

        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={4}
                width="100%">
                <Box className={FLEX_ITEM_DETAILS_WIDTH}>
                    <Typography variant="h6" component="div" gutterBottom>
                        {activeIter.type[0].toUpperCase() +
                            activeIter.type.slice(1)}{' '}
                        Count {activeIter.countId}
                        <Typography component="span" variant="body2">
                            {' '}
                            — {ITER_NAMES[activeIter.iterationId]} Iteration
                        </Typography>
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>{activeIter.status}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Requester</TableCell>
                                    <TableCell>
                                        {activeIter.requestedBy}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Assigned</TableCell>
                                    <TableCell>
                                        {activeIter.assignedAt?.toLocaleString() ?? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary">
                                                Unassigned
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Sample</TableCell>
                                    <TableCell>
                                        {activeIter.sample
                                            ? activeIter.sample
                                                .map(x => x.sku)
                                                .join(', ')
                                            : 'ALL BINS'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Description</TableCell>
                                    <TableCell>
                                        {activeIter.description ?? ''}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <Typography
                    variant="body1"
                    fontWeight="bold"
                    className={FLEX_ITEM_DETAILS_WIDTH}>
                    Once you begin counting, you cannot make changes to your
                    truck&apos;s inventory: i.e. receiving, invoicing, returns,
                    or transfers.
                </Typography>
                <Box className={FLEX_ITEM_DETAILS_WIDTH}>
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={startCounting}
                        data-testid="start-counting"
                        >
                        {action}
                    </Button>
                </Box>
            </Box>
        )
    }, [activeIter])

    function startCounting() {
        if (activeIter === null) return

        let path = activeIter.sample ? '/inventory/counts/sample' : '/inventory/counts/bin'

        /* assign the count iteration when it is pending */
        if (activeIter.status === 'pending') {
            API.post(`inventory/counts/${activeIter.countId}/assign`)
                .then(r => {
                    let iter = {...activeIter}
                    iter.assignedAt = r.data.iteration.assigned_at
                    iter.status = r.data.iteration.status
                    window.sessionStorage.setItem('activeIter', JSON.stringify(iter))
                    window.sessionStorage.setItem('submissionSuccess', 'false')
                    navigate(path)
                })
                .catch(error => {
                    webEvents.error(
                        error.message,
                        {iteration: activeIter},
                        error.stack
                    )
                    const causeMessage = error.cause?.messages?.[0] || error.messages?.[0] || "Unknown error"

                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                })
        } else {
            window.sessionStorage.setItem('submissionSuccess', 'false')
            navigate(path)
        }
    }

    return (
        <>
            <Box textAlign="center" mb={4}>
                {alertOpen && (
                    <Alert
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            width: '80%',
                            mx: 'auto',
                            alignItems: 'flex-start',
                            my: 1
                        }}
                        icon={<CheckIcon fontSize="inherit" />} severity="success"
                        onClose={() => setAlertOpen(false)}>
                        Report successfully submitted for review!
                    </Alert>
                )}
                <Typography variant="h4" component="h1" gutterBottom sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mx: 'auto',
                    alignItems: 'flex-start',
                    my: 3
                }}
                >
                    {getTruck()} has {activeIter ? 'an' : 'no'} active count
                </Typography>
                {truckName ? activeIter ? IterationDetails : null :
                    <h3>You don't have an assigned Truck to perform a count</h3>}
            </Box>
        </>
    )
}

export default Conduct
